import type { BinderWord } from '../stores/binderStore';

// Word Binder filters and sorts (2026-10-22, Odin's ruling: the Binder points
// at the next thing to do). Pure, so a filter's count, its list and Next to
// review all come from the same code. Reads the guide GET /api/binder returns
// for every word in one call.
// Decluttered 2026-10-22 (Odin's ruling): In progress (same as Waiting on me),
// Running now (the top bar badge shows it) and Difficulty (a sort) left the
// filter menu; a row shows only its count, the next stage is its tooltip.
// Pronunciation check (2026-10-25): the words Publish would refuse for their
// pronunciation (empty, needs_check or never checked).

export type BinderFilter =
  | 'waiting' | 'fixes' | 'pronunciation' | 'not_started' | 'finished' | 'scheduled' | 'played' | 'all';
export type BinderSort = 'az' | 'date' | 'closest' | 'recent' | 'difficulty';

export const FILTERS: { id: BinderFilter; label: string; gameOnly?: boolean }[] = [
  { id: 'waiting', label: 'Waiting on me' },
  { id: 'fixes', label: 'Needs fixes' },
  { id: 'pronunciation', label: 'Pronunciation check' },
  { id: 'not_started', label: 'Not started' },
  // Content approved, not yet scheduled.
  { id: 'finished', label: 'Finished' },
  { id: 'scheduled', label: 'Scheduled', gameOnly: true },
  { id: 'played', label: 'Played', gameOnly: true },
  { id: 'all', label: 'All' },
];

export const SORTS: { id: BinderSort; label: string }[] = [
  { id: 'closest', label: 'Closest to finished' },
  { id: 'az', label: 'A to Z' },
  { id: 'date', label: 'Schedule date' },
  { id: 'recent', label: 'Last worked on' },
  { id: 'difficulty', label: 'Difficulty' },
];

export const DEFAULT_FILTER: BinderFilter = 'waiting';
export const DEFAULT_SORT: BinderSort = 'closest';

// Stage ids as the Binder names them, the Content page tab names (2026-10-30;
// the Hook is retired and no longer comes next).
const STAGE_NAME: Record<string, string> = { '1': '1 Meaning', '1b': '2 Fakes', rv: '3 Reveals', as: '4 Assignment', hk: 'Hook', '2': '5 Examples', '3': '6 Win/Lose' };

/** "3/7", or "done" once no stage is left. */
export function stepText(w: BinderWord): string {
  const g = w.guide;
  if (!g) return '';
  if (!g.next) return 'done';
  return `${g.done}/${g.total}`;
}

/** "next: 3 Reveals" for the count's tooltip, or '' once no stage is left. */
export function nextText(w: BinderWord): string {
  const next = w.guide?.next;
  return next ? `next: ${STAGE_NAME[next] || next}` : '';
}

/** The next stage has content that is not approved yet (generated or needs fixes). */
export const isWaiting = (w: BinderWord) =>
  !!w.guide?.next && (w.guide.next_status === 'generated' || w.guide.next_status === 'needs_fixes');

export function matches(w: BinderWord, f: BinderFilter): boolean {
  const g = w.guide;
  switch (f) {
    case 'waiting': return isWaiting(w);
    case 'fixes': return !!g?.needs_fixes;
    case 'pronunciation': return !!w.pronunciation?.needs_check;
    case 'not_started': return !g?.started;
    case 'finished': return !!w.content_approved_at && !w.game_date && !w.played_on;
    case 'scheduled': return !!w.game_date && !w.played_on;
    case 'played': return !!w.played_on;
    default: return true;
  }
}

const byWord = (a: BinderWord, b: BinderWord) => a.word.localeCompare(b.word, undefined, { sensitivity: 'base' });
// Missing values sort last in every order.
const last = <T,>(v: T | null | undefined, missing: T) => (v === null || v === undefined || v === '' ? missing : v);

export function compare(sort: BinderSort): (a: BinderWord, b: BinderWord) => number {
  switch (sort) {
    case 'date':
      return (a, b) => last(a.game_date || a.played_on, '9999').localeCompare(last(b.game_date || b.played_on, '9999')) || byWord(a, b);
    case 'closest': {
      // Share of stages done, highest first; a finished word sorts after the open ones.
      const share = (w: BinderWord) => (!w.guide ? -1 : !w.guide.next ? -0.5 : w.guide.done / (w.guide.total || 1));
      return (a, b) => share(b) - share(a) || byWord(a, b);
    }
    case 'recent':
      return (a, b) => last(b.guide?.last_worked_at, '').localeCompare(last(a.guide?.last_worked_at, '')) || byWord(a, b);
    case 'difficulty':
      return (a, b) => last(a.guide?.difficulty, 99) - last(b.guide?.difficulty, 99) || byWord(a, b);
    default:
      return byWord;
  }
}

/** The words one filter shows, in the given sort. Scheduled is always in date order. */
export function view<T extends BinderWord>(words: T[], f: BinderFilter, sort: BinderSort): T[] {
  return words.filter((w) => matches(w, f)).sort(compare(f === 'scheduled' ? 'date' : sort));
}

const KEY = 'waw:binder-view';

// A remembered filter that no longer exists (progress, running, difficulty)
// falls back to Waiting on me.
export function readView(): { filter: BinderFilter; sort: BinderSort } {
  const out = { filter: DEFAULT_FILTER, sort: DEFAULT_SORT };
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (p && FILTERS.some((x) => x.id === p.filter)) out.filter = p.filter;
    if (p && SORTS.some((x) => x.id === p.sort)) out.sort = p.sort;
  } catch {
    // Private mode or bad JSON: the defaults.
  }
  return out;
}

export function writeView(v: { filter: BinderFilter; sort: BinderSort }): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // Storage blocked: the view still works, it just is not remembered.
  }
}
